import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Building2, ArrowRight } from 'lucide-react';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (sectionId === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        // Add offset for fixed header
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
    setMobileMenuOpen(false);
  };

  const navVariants = {
    hidden: { y: -100, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const mobileMenuVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: { 
      opacity: 1, 
      height: "auto",
      transition: {
        duration: 0.4,
        ease: "easeInOut"
      }
    },
    exit: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3
      }
    }
  };

  const menuItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        duration: 0.4
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.5
      }
    }
  };

  return (
    <motion.nav
      variants={navVariants}
      initial="hidden"
      animate="visible"
      className={`fixed w-full z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-slate-900/95 backdrop-blur-2xl border-b border-white/5 py-3' 
          : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <motion.div 
          className="flex items-center gap-3 cursor-pointer"
          whileHover={{ scale: 1.02 }}
          onClick={() => scrollToSection('home')}
          whileTap={{ scale: 0.98 }}
        >
          <div className="bg-gradient-to-br from-emerald-500 to-teal-500 p-3 rounded-2xl">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="font-bold text-xl tracking-tight text-white">Done Commercials</div>
            <div className="text-xs text-slate-400 tracking-wide">PREMIUM FINANCING</div>
          </div>
        </motion.div>

        <div className="hidden lg:flex items-center gap-12">
          {['home', 'services', 'process', 'about', 'contact'].map((item) => (
            <motion.button
              key={item}
              onClick={() => scrollToSection(item)}
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors relative group capitalize"
              whileHover={{ y: -2 }}
              whileTap={{ y: 0 }}
            >
              {item === 'home' ? 'Home' : 
               item === 'services' ? 'Services' :
               item === 'process' ? 'Process' :
               item === 'about' ? 'About' : 'Contact'}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 group-hover:w-full transition-all duration-300" />
            </motion.button>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <motion.button
            onClick={() => scrollToSection('contact')}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-3 rounded-full font-semibold text-sm text-white flex items-center gap-2 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95, y: 0 }}
          >
            Contact Us <ArrowRight className="w-4 h-4" />
          </motion.button>
        </div>

        <motion.button 
          className="lg:hidden text-white"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </motion.button>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            variants={mobileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="lg:hidden bg-slate-900/95 backdrop-blur-2xl border-t border-white/5 p-6 overflow-hidden"
          >
            <motion.div variants={containerVariants}>
              {['home', 'services', 'process', 'about', 'contact'].map((item, index) => (
                <motion.button
                  key={item}
                  variants={menuItemVariants}
                  onClick={() => scrollToSection(item)}
                  className="block w-full text-left py-3 text-slate-300 hover:text-white border-b border-white/10 last:border-b-0 capitalize"
                  whileHover={{ x: 10 }}
                >
                  {item === 'home' ? 'Home' : 
                   item === 'services' ? 'Services' :
                   item === 'process' ? 'Process' :
                   item === 'about' ? 'About' : 'Contact'}
                </motion.button>
              ))}
              <motion.div variants={menuItemVariants} className="pt-4 border-t border-white/10">
                <motion.button
                  onClick={() => scrollToSection('contact')}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-3 rounded-full font-semibold text-sm text-white flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Contact Us <ArrowRight className="w-4 h-4" />
                </motion.button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Header;